// src/features/analytics/useAnalyticsConcurrency.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi, type AnalyticsViewScope } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS, type AnalyticsInterval } from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, ConcurrencyData } from './types';

export interface UseAnalyticsConcurrencyResult {
  concurrency: AnalyticsEnvelope<ConcurrencyData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAnalyticsConcurrency(
  scope: AnalyticsViewScope | null,
  range: UtcRange,
  interval: AnalyticsInterval
): UseAnalyticsConcurrencyResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.concurrency(
      scope?.kind ?? 'account',
      scope?.cid ?? 0,
      range.from,
      range.to,
      interval
    ),
    queryFn: () => AnalyticsApi.getConcurrency(scope!, range, interval),
    enabled: scope !== null,
    staleTime: 60_000,
  });

  return {
    concurrency: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
